import { createEffect, createMemo, createSignal, For, onMount, Show } from 'solid-js';
import { A, useParams } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useItemName } from '../hooks/useItemName';
import { EmptyState } from '../components/EmptyState';
import FeatureGate from '../components/FeatureGate';
import BotSelector from '../components/BotSelector';
import StorageSelect from '../components/StorageSelect';
import LocationPicker from '../components/LocationPicker';
import ItemStackView from '../components/ItemStackView';
import Text from '../components/Text';
import { Citybuild } from '../models/Citybuild';
import { fromIdentifier } from '../services/itemRefAdapters';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { useInventoryApi, type StorageInfo } from '../services/inventoryApi';
import type { SchematicBuildPlanDto, SchematicMaterialDto } from '../api/generated/schemas';
import styles from './SchematicBuild.module.css';

const CITYBUILD_OPTIONS = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

export default function SchematicBuild() {
    const params = useParams();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const inv = useInventoryApi();
    const resolver = useCraftingResolver();
    const { stackName } = useItemName();

    const [plan, setPlan] = createSignal<SchematicBuildPlanDto | null>(null);
    const [loading, setLoading] = createSignal(true);
    const [planLoading, setPlanLoading] = createSignal(false);
    const [missing, setMissing] = createSignal(false);

    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [storageId, setStorageId] = createSignal('');
    const [botId, setBotId] = createSignal<string | null>(null);
    type LayerMode = 'Off' | 'Lax' | 'Strict';
    const [layerMode, setLayerMode] = createSignal<LayerMode>('Lax');
    type BuildMode = 'Pathfinder' | 'Koffermatic';
    const [buildMode, setBuildMode] = createSignal<BuildMode>('Pathfinder');
    const [building, setBuilding] = createSignal(false);

    const [siteCitybuild, setSiteCitybuild] = createSignal<number>(Citybuild.Nature);
    const [siteLocation, setSiteLocation] = createSignal('');
    const [savingSite, setSavingSite] = createSignal(false);

    const schematic = () => plan()?.schematic ?? null;

    const loadPlan = async (sid: string) => {
        setPlanLoading(true);
        const res = await api.schematics.getBuildPlan(params.id, sid ? { storageId: sid } : undefined);
        if (isOk(res.data)) {
            setPlan(res.data.value as unknown as SchematicBuildPlanDto);
            setMissing(false);
        } else {
            // Only a 404 means the schematic is gone; any other failure keeps whatever is on screen and
            // says what went wrong, rather than claiming the schematic does not exist.
            setMissing((res.data?.status as number | undefined) === 404);
            toast?.error(firstError(res.data) ?? t('SCHEMATICS_PLAN_ERROR'));
        }
        setPlanLoading(false);
        setLoading(false);
    };

    onMount(() => {
        void loadPlan('');
        void (async () => setStorages(await inv.fetchStorages()))();
    });

    // Seeding once per schematic, not on every plan refresh: picking a storage refetches the plan, and
    // re-seeding there would throw away a build site the operator had typed but not yet saved.
    let seededSchematicId = '';
    createEffect(() => {
        const loadedSchematic = schematic();
        if (!loadedSchematic || loadedSchematic.id === seededSchematicId) return;
        seededSchematicId = loadedSchematic.id;
        setSiteCitybuild(loadedSchematic.buildCitybuild);
        setSiteLocation(loadedSchematic.buildLocation ?? '');
    });

    const onStorageChange = (sid: string) => {
        setStorageId(sid);
        void loadPlan(sid);
    };

    const saveBuildSite = async () => {
        setSavingSite(true);
        const res = await api.schematics.setBuildSite(params.id, {
            citybuild: siteCitybuild(),
            location: siteLocation() || null,
        });
        setSavingSite(false);
        if (isOk(res.data)) {
            const savedSchematic = res.data.value as unknown as SchematicBuildPlanDto['schematic'];
            // A cleared location falls back to the capture's own, so re-seed from what came back
            // instead of leaving the form showing a blank the bot will not use.
            seededSchematicId = '';
            setPlan(current => (current ? { ...current, schematic: savedSchematic } : current));
            toast?.success(t('SCHEMATICS_SITE_SAVED'));
        } else {
            toast?.error(firstError(res.data) ?? t('SCHEMATICS_SITE_SAVE_FAILED'));
        }
    };

    const siteDirty = createMemo(() => {
        const loadedSchematic = schematic();
        if (!loadedSchematic) return false;
        return loadedSchematic.buildCitybuild !== siteCitybuild()
            || (loadedSchematic.buildLocation ?? '') !== siteLocation();
    });

    const startBuild = async () => {
        const bid = botId();
        if (!bid || !storageId()) { toast?.error(t('SCHEMATICS_BUILD_PICK')); return; }
        if (siteDirty()) { toast?.error(t('SCHEMATICS_SITE_UNSAVED')); return; }
        if (storageShort() && !await showConfirm(t('SCHEMATICS_BUILD_SHORT_CONFIRM'))) return;
        setBuilding(true);
        const res = await api.botTask.runTask(bid, buildMode() === 'Koffermatic'
            ? {
                plugin: 'Pathfinding',
                task: 'KoffermaticPrinter',
                args: { storedSchematicId: params.id, storageId: storageId() },
            }
            : {
                plugin: 'Pathfinding',
                task: 'BuildSchematicFromStorage',
                args: { storedSchematicId: params.id, storageId: storageId(), layerMode: layerMode() },
            });
        setBuilding(false);
        if (isOk(res.data)) toast?.success(t('SCHEMATICS_BUILD_STARTED'));
        else toast?.error(firstError(res.data) ?? t('SCHEMATICS_BUILD_FAILED'));
    };

    const materialStack = (m: SchematicMaterialDto) => {
        const r = resolver();
        const def = r?.getAllItems?.()?.find(i => i.id === m.itemId && i.metadata === m.meta)
            ?? r?.getAllItems?.()?.find(i => i.id === m.itemId);
        return fromIdentifier(def ? `minecraft:${def.name}` : `minecraft:${m.itemId}`, m.meta, r);
    };

    // The panel costs the whole file, while the bot surveys the site first and costs only what is still
    // missing, so a part-built schematic reads short here and is fine there. Warn, do not block.
    const storageShort = () => plan()?.availabilityKnown === true && plan()?.ready === false;

    const buildDisabled = () => building() || !botId() || !storageId() || siteDirty();

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <A href="/schematics" class="btn btn-secondary">
                    <i class="fa-solid fa-arrow-left" /> <Text val="SCHEMATICS_BACK_TO_LIST" />
                </A>
            </div>

            <Show when={!loading()} fallback={
                <div class={styles.columns}>
                    <For each={[0, 1]}>{() => <div class={styles.skeletonCard} />}</For>
                </div>
            }>
                <Show when={schematic()} fallback={
                    <EmptyState
                        illustration="generic"
                        title={t(missing() ? 'SCHEMATICS_NOT_FOUND_TITLE' : 'SCHEMATICS_PLAN_ERROR_TITLE')}
                        hint={t(missing() ? 'SCHEMATICS_NOT_FOUND_HINT' : 'SCHEMATICS_PLAN_ERROR_HINT')}
                    />
                }>
                    {(loadedSchematic) => (
                        <>
                            <div class={styles.titleRow}>
                                <h1><i class="fa-solid fa-cubes" /> {loadedSchematic().name}</h1>
                                <span class={styles.blockCount}>
                                    {plan()!.blockCount} <Text val="SCHEMATICS_BLOCKS" />
                                    {' · '}
                                    {plan()!.layerCount} <Text val="SCHEMATICS_LAYERS" />
                                </span>
                            </div>

                            <div class="fcard">
                                <div class={styles.fields}>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_SIZE" /></span>
                                        <span class={styles.fieldValue}>
                                            {loadedSchematic().width} x {loadedSchematic().height} x {loadedSchematic().length}
                                        </span>
                                    </div>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_ANCHOR" /></span>
                                        <span class={styles.fieldValue}>
                                            {loadedSchematic().anchorX}, {loadedSchematic().anchorY}, {loadedSchematic().anchorZ}
                                        </span>
                                    </div>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_CAPTURED_AT" /></span>
                                        <span class={styles.fieldValue}>
                                            {loadedSchematic().citybuildName}
                                            {loadedSchematic().location ? ` / ${loadedSchematic().location}` : ''}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class={styles.columns}>
                                <div class="fcard v" style={{ gap: 'var(--spacing-lg)' }}>
                                    <h2 class={styles.sectionTitle}><Text val="SCHEMATICS_SECTION_SETUP" /></h2>

                                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_BUILD_BOT" /></span>
                                        <BotSelector storageKey="schematic_build_bot" onSelect={(id) => setBotId(id)} />
                                    </label>

                                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_BUILD_STORAGE" /></span>
                                        <StorageSelect
                                            storages={storages()}
                                            value={storageId()}
                                            onChange={onStorageChange}
                                            placeholder={t('SCHEMATICS_BUILD_STORAGE_PICK')}
                                        />
                                    </label>

                                    <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_SITE_TITLE" /></span>
                                        <p class={styles.help}><Text val="SCHEMATICS_SITE_HELP" /></p>
                                        <select
                                            value={siteCitybuild()}
                                            onChange={(e) => setSiteCitybuild(parseInt(e.currentTarget.value))}
                                        >
                                            <For each={CITYBUILD_OPTIONS}>{(key) =>
                                                <option value={Citybuild[key]}>{key}</option>
                                            }</For>
                                        </select>
                                        <LocationPicker value={siteLocation()} onChange={setSiteLocation} optional />
                                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                                            <Show when={siteDirty()}>
                                                <span class={styles.unsaved}><Text val="SCHEMATICS_SITE_DIRTY" /></span>
                                            </Show>
                                            <button
                                                type="button"
                                                class="btn btn-secondary btn-sm"
                                                disabled={savingSite() || !siteDirty()}
                                                onClick={() => void saveBuildSite()}
                                            >
                                                <Show when={savingSite()} fallback={<Text val="SCHEMATICS_SITE_SAVE" />}>
                                                    <i class="fa-solid fa-spinner fa-spin" />
                                                </Show>
                                            </button>
                                        </div>
                                    </div>

                                    <FeatureGate flag="SCHEMATIC_PRINTER">
                                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <span class={styles.fieldLabel}><Text val="SCHEMATICS_BUILD_MODE" /></span>
                                            <select value={buildMode()} onChange={(e) => setBuildMode(e.currentTarget.value as BuildMode)}>
                                                <option value="Pathfinder">{t('SCHEMATICS_BUILD_MODE_PATHFINDER')}</option>
                                                <option value="Koffermatic">{t('SCHEMATICS_BUILD_MODE_KOFFERMATIC')}</option>
                                            </select>
                                            <span class={styles.help}>
                                                <Text val={`SCHEMATICS_BUILD_MODE_HELP_${buildMode().toUpperCase()}`} />
                                            </span>
                                        </label>
                                    </FeatureGate>

                                    <Show when={buildMode() === 'Pathfinder'}>
                                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_LAYER_MODE" /></span>
                                        <select value={layerMode()} onChange={(e) => setLayerMode(e.currentTarget.value as LayerMode)}>
                                            <option value="Strict">{t('SCHEMATICS_LAYER_MODE_STRICT')}</option>
                                            <option value="Lax">{t('SCHEMATICS_LAYER_MODE_LAX')}</option>
                                            <option value="Off">{t('SCHEMATICS_LAYER_MODE_OFF')}</option>
                                        </select>
                                        <span class={styles.help}>
                                            <Text val={`SCHEMATICS_LAYER_MODE_HELP_${layerMode().toUpperCase()}`} />
                                        </span>
                                    </label>
                                    </Show>

                                    <div class="h" style={{ 'justify-content': 'flex-end' }}>
                                        <button
                                            type="button"
                                            class="btn btn-primary"
                                            disabled={buildDisabled()}
                                            onClick={() => void startBuild()}
                                        >
                                            <Show when={building()} fallback={<><i class="fa-solid fa-hammer" /> <Text val="SCHEMATICS_BUILD_START" /></>}>
                                                <i class="fa-solid fa-spinner fa-spin" />
                                            </Show>
                                        </button>
                                    </div>
                                </div>

                                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                                    <div class={styles.sectionHeader}>
                                        <h2 class={styles.sectionTitle}><Text val="SCHEMATICS_BUILD_MATERIALS" /></h2>
                                        <Show when={plan()!.availabilityKnown}>
                                            <span classList={{ [styles.ready]: plan()!.ready, [styles.short]: !plan()!.ready }}>
                                                <Text val={plan()!.ready ? 'SCHEMATICS_READY' : 'SCHEMATICS_SHORT'} />
                                            </span>
                                        </Show>
                                    </div>

                                    <Show when={!plan()!.availabilityKnown}>
                                        <p class={styles.help}><Text val="SCHEMATICS_PICK_STORAGE_HINT" /></p>
                                    </Show>

                                    <Show
                                        when={!planLoading()}
                                        fallback={<div class={styles.centerWait}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
                                    >
                                        <div class={styles.matList}>
                                            <For each={plan()!.materials}>{(m) => {
                                                const stack = createMemo(() => materialStack(m));
                                                return (
                                                <div class={styles.matRow}>
                                                    <ItemStackView stack={stack()} />
                                                    <span class={styles.matName}>{stackName(stack())}</span>
                                                    <Show
                                                        when={plan()!.availabilityKnown}
                                                        fallback={<span class={styles.matNeed}>x {m.need}</span>}
                                                    >
                                                        <span classList={{ [styles.ok]: m.sufficient, [styles.bad]: !m.sufficient }}>
                                                            {m.infinite ? t('SCHEMATICS_INFINITE') : m.have} / {m.need}
                                                        </span>
                                                    </Show>
                                                </div>
                                                );
                                            }}</For>
                                        </div>
                                    </Show>
                                </div>
                            </div>
                        </>
                    )}
                </Show>
            </Show>
        </div>
    );
}
